import useSWR from 'swr';
import { KeyedMutator } from 'swr/_internal';
import { GrowconnectService, GrowRegistrationStatus, GrowRegistrationStatusOut } from '../../api';

const POLL_INTERVAL_MS = 300000;

type UseGrowRegistrationStatusProps = {
  shouldNotMakeRequest: boolean;
};
type UseGrowRegistrationStatusResult = {
  registration?: GrowRegistrationStatusOut;
  error: any;
  isLoading: boolean;
  refetchRegistration: KeyedMutator<GrowRegistrationStatusOut>;
};
const statusFetcher = async () => GrowconnectService.getGrowRegistrationStatus();

/**
 * The status endpoint asserts an Israeli account and 403s otherwise, so callers pass
 * `shouldNotMakeRequest` for accounts that can never register -- same conditional-key shape as
 * useGetAccountAuthenticated.
 *
 * Polling is deliberately slow. Approval waits on a person at Grow, so a fast refresh only makes
 * the page feel like something is happening when nothing is; the state that matters is far more
 * likely to be discovered on a later page load than watched live.
 */
export const useGrowRegistrationStatus = ({
  shouldNotMakeRequest,
}: UseGrowRegistrationStatusProps): UseGrowRegistrationStatusResult => {
  const { data, error, isLoading, mutate } = useSWR<GrowRegistrationStatusOut>(
    shouldNotMakeRequest ? null : { opId: 'getGrowRegistrationStatus' },
    statusFetcher,
    {
      refreshInterval: (latest) => (latest?.status === GrowRegistrationStatus.PENDING ? POLL_INTERVAL_MS : 0),
      revalidateOnFocus: true,
    },
  );

  return { registration: data, error, isLoading, refetchRegistration: mutate };
};
